import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '@/config/api';
import { getAttributionMetadata } from '@/lib/utm';
import { notifyAccountDeleted } from '@/lib/extensionBridge';

interface User {
  id: string;
  email: string;
  name: string;
  email_verified: boolean;
  profile_picture_url: string;
  is_new_user?: boolean;
}

interface License {
  license_key: string;
  plan: string;
  cancel_at_period_end?: boolean;
  expires_at?: string | null;
}

interface AuthContextType {
  user: User | null;
  license: License | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (googleToken: string, intent?: 'paid') => Promise<{ user: User; license: License | null }>;
  loginWithGoogle: (authCode: string, intent?: 'paid') => Promise<{ user: User; license: License | null }>;
  loginWithLinkedIn: (authCode: string, intent?: 'paid') => Promise<{ user: User; license: License | null }>;
  logout: () => void;
  refreshLicense: () => Promise<void>;
  updateLicense: (licenseKey: string, plan: string) => void;
  deleteAccount: (confirmText: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [license, setLicense] = useState<License | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user data from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('xrepl_user');
    const storedLicense = localStorage.getItem('xrepl_license');
    
    if (storedUser && storedLicense) {
      try {
        setUser(JSON.parse(storedUser));
        setLicense(JSON.parse(storedLicense));
      } catch (error) {
        console.error('Failed to parse stored auth data:', error);
        localStorage.removeItem('xrepl_user');
        localStorage.removeItem('xrepl_license');
      }
    }
    
    setIsLoading(false);
  }, []);

  const login = async (googleToken: string, intent?: 'paid'): Promise<{ user: User; license: License | null }> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH_REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken: googleToken, ...(intent && { intent }), attribution: getAttributionMetadata() }),
      });

      if (!response.ok) {
        // Get error details from response
        let errorMessage = 'Authentication failed';
        let errorDetails = null;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          errorDetails = errorData.details;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Authentication failed:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          details: errorDetails,
        });
        // Include details in error message if available
        const fullErrorMessage = errorDetails 
          ? `${errorMessage}: ${errorDetails}` 
          : errorMessage;
        throw new Error(fullErrorMessage);
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        setUser(data.user);
        // License may be null if intent='paid' (user will get license after Stripe checkout)
        if (data.license) {
          setLicense(data.license);
          localStorage.setItem('xrepl_license', JSON.stringify(data.license));
        }
        
        // Store in localStorage
        localStorage.setItem('xrepl_user', JSON.stringify(data.user));
        
        return { user: data.user, license: data.license };
      } else {
        throw new Error(data.message || 'Invalid response from server');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const loginWithLinkedIn = useCallback(async (authCode: string, intent?: 'paid'): Promise<{ user: User; license: License | null }> => {
    try {
      const redirectUri = import.meta.env.VITE_LINKEDIN_REDIRECT_URI || `${window.location.origin}/auth/linkedin/callback`;
      
      const response = await fetch(API_ENDPOINTS.AUTH_LINKEDIN_REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: authCode,
          redirectUri: redirectUri,
          ...(intent && { intent }),
          attribution: getAttributionMetadata(),
        }),
      });

      if (!response.ok) {
        let errorMessage = 'LinkedIn authentication failed';
        let errorDetails = null;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          errorDetails = errorData.details;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('LinkedIn authentication failed:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          details: errorDetails,
        });
        const fullErrorMessage = errorDetails 
          ? `${errorMessage}: ${errorDetails}` 
          : errorMessage;
        throw new Error(fullErrorMessage);
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        setUser(data.user);
        // License may be null if intent='paid' (user will get license after Stripe checkout)
        if (data.license) {
          setLicense(data.license);
          localStorage.setItem('xrepl_license', JSON.stringify(data.license));
        }
        
        localStorage.setItem('xrepl_user', JSON.stringify(data.user));
        
        return { user: data.user, license: data.license };
      } else {
        throw new Error(data.message || 'Invalid response from server');
      }
    } catch (error: any) {
      console.error('LinkedIn login error:', error);
      throw error;
    }
  }, []);

  const loginWithGoogle = useCallback(async (authCode: string, intent?: 'paid'): Promise<{ user: User; license: License | null }> => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      
      const response = await fetch(API_ENDPOINTS.AUTH_GOOGLE_REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: authCode,
          redirectUri: redirectUri,
          ...(intent && { intent }),
          attribution: getAttributionMetadata(),
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Google authentication failed';
        let errorDetails = null;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          errorDetails = errorData.details;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        console.error('Google authentication failed:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage,
          details: errorDetails,
        });
        const fullErrorMessage = errorDetails 
          ? `${errorMessage}: ${errorDetails}` 
          : errorMessage;
        throw new Error(fullErrorMessage);
      }

      const data = await response.json();
      
      if (data.success && data.user) {
        setUser(data.user);
        // License may be null if intent='paid' (user will get license after Stripe checkout)
        if (data.license) {
          setLicense(data.license);
          localStorage.setItem('xrepl_license', JSON.stringify(data.license));
        }
        
        localStorage.setItem('xrepl_user', JSON.stringify(data.user));
        
        return { user: data.user, license: data.license };
      } else {
        throw new Error(data.message || 'Invalid response from server');
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setLicense(null);
    localStorage.removeItem('xrepl_user');
    localStorage.removeItem('xrepl_license');
  }, []);

  const refreshLicense = useCallback(async () => {
    const storedUser = localStorage.getItem('xrepl_user');
    if (!storedUser) return;
    try {
      const user = JSON.parse(storedUser) as User;
      const response = await fetch(API_ENDPOINTS.USER_PROFILE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      const data = await response.json();
      if (!response.ok) {
        console.error('Profile API error:', response.status, data?.error, data?.license_error ?? data?.license_code ?? data?.license_details ?? data);
      }
      if (data.success) {
        if (data.license) {
          const cancelScheduled = data.license.cancel_at_period_end === true || String(data.license.cancel_at_period_end || '').toLowerCase() === 'true';
          const licenseData = {
            license_key: data.license.license_key,
            plan: data.license.plan || 'free',
            cancel_at_period_end: cancelScheduled,
            expires_at: data.license.expires_at ?? null,
          };
          setLicense(licenseData);
          localStorage.setItem('xrepl_license', JSON.stringify(licenseData));
        } else {
          // No active license (e.g. all inactive) — clear cached license so UI does not treat any plan as "current"
          setLicense(null);
          localStorage.removeItem('xrepl_license');
        }
      }
    } catch (error) {
      console.error('Failed to refresh license:', error);
    }
  }, []);

  // Directly update license state and localStorage (used by SuccessPage after checkout)
  const updateLicense = useCallback((licenseKey: string, plan: string) => {
    const licenseData = { license_key: licenseKey, plan };
    setLicense(licenseData);
    localStorage.setItem('xrepl_license', JSON.stringify(licenseData));
  }, []);

  const deleteAccount = useCallback(async (confirmText: string) => {
    if (!user?.id) throw new Error('Not authenticated');
    const response = await fetch(API_ENDPOINTS.AUTH_DELETE_ACCOUNT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, confirmText }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to delete account');
    }
    // Clear local state
    setUser(null);
    setLicense(null);
    localStorage.removeItem('xrepl_user');
    localStorage.removeItem('xrepl_license');
    // Notify extension to clear its data (fire and forget)
    notifyAccountDeleted().catch(() => {
      // Extension may not be installed - that's OK
    });
  }, [user?.id]);

  const value = {
    user,
    license,
    isAuthenticated: !!user,
    isLoading,
    login,
    loginWithGoogle,
    loginWithLinkedIn,
    logout,
    refreshLicense,
    updateLicense,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
